(function () {
  'use strict';

  var REQUEST_TIMEOUT = 60000;

  // Shared contract utilities. Keep the guided designer's IDs, proof and JSON shape.
  function createReference() {
    if (!window.crypto || !window.crypto.getRandomValues) throw new Error('This browser cannot create a secure proof reference. Please use a current browser.');
    var random = Array.prototype.map.call(window.crypto.getRandomValues(new Uint8Array(8)), function (value) {
      return value.toString(16).padStart(2, '0');
    }).join('');
    return 'ERS-CS-' + Date.now().toString(36).toUpperCase() + '-' + random.toUpperCase();
  }

  function metadata(reference, email, order, designDocument, submittedSnapshot, extra) {
    var value = {
      schemaVersion: 1, attemptId: reference, reference: reference, email: email,
      order: order, designDocument: designDocument, submittedSnapshot: submittedSnapshot
    };
    if (extra) value.productPage = extra;
    return JSON.stringify(value);
  }

  function buildProofCanvas(artwork, reference) {
    if (!artwork || !artwork.front || !artwork.back) return null;
    var canvas = document.createElement('canvas');
    canvas.width = 1800;
    canvas.height = 560;
    var context = canvas.getContext('2d');
    context.fillStyle = '#f7f4ed';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#173f3a';
    context.font = '700 34px Arial, sans-serif';
    context.fillText('ERS CUSTOM CHOPSTICK SLEEVE PROOF', 60, 48);
    context.font = '500 22px Arial, sans-serif';
    context.fillText(reference, 60, 78);
    context.font = '700 30px Arial, sans-serif';
    context.fillText('FRONT', 60, 120);
    context.drawImage(artwork.front, 60, 138, 1680, 160);
    context.fillText('BACK', 60, 352);
    context.drawImage(artwork.back, 60, 370, 1680, 160);
    return canvas;
  }

  function protectContact(form, emailInput, isDestroyed) {
    if (!form || typeof form.requestSubmit !== 'function') {
      return Promise.reject(new Error('Shopify proof-request protection is unavailable. Your work is saved; please refresh and try again.'));
    }
    function bound() { return form.hasAttribute('data-hcaptcha-bound') || form.hasAttribute('data-recaptcha-bound'); }
    if (bound()) return Promise.resolve();
    if (emailInput) emailInput.dispatchEvent(new Event('change', { bubbles: true }));
    return new Promise(function (resolve, reject) {
      var startedAt = Date.now();
      var timer = window.setInterval(function () {
        var interrupted = isDestroyed && isDestroyed();
        if (bound() && !interrupted) { window.clearInterval(timer); resolve(); }
        else if (interrupted || Date.now() - startedAt >= 15000) {
          window.clearInterval(timer);
          reject(new Error('Shopify proof-request protection could not start. Your work is saved; please refresh and try again.'));
        }
      }, 50);
    });
  }

  function storageError(code, message, details) {
    var error = new Error(message);
    error.code = code;
    error.details = details || null;
    return error;
  }

  function verifyReceipt(payload, body, endpoint) {
    var artworkUrl;
    try {
      artworkUrl = new URL(body.artworkUrl);
    } catch (error) {
      throw storageError('PROOF_STORAGE_RECEIPT_INVALID', 'Artwork storage could not be verified. Your work is saved; please try again.');
    }
    if (
      String(body.reference) !== String(payload.reference) ||
      String(body.submissionId) !== String(payload.attemptId) ||
      artworkUrl.origin !== new URL(endpoint).origin ||
      artworkUrl.pathname.indexOf('/proof/' + encodeURIComponent(payload.attemptId) + '/') !== 0
    ) {
      throw storageError('PROOF_STORAGE_RECEIPT_INVALID', 'Artwork storage could not be verified. Your work is saved; please try again.');
    }
    // Fail closed against an older Worker that would ignore source files.
    var submissionUrl = null;
    if (Array.isArray(payload.sourceFiles)) {
      try { submissionUrl = new URL(body.submissionUrl); } catch (error) {}
      var expected = payload.sourceFiles.map(function (source) { return source.id; }).sort();
      var received = Array.isArray(body.sourceIds) ? body.sourceIds.slice().sort() : [];
      if (!submissionUrl || submissionUrl.origin !== artworkUrl.origin ||
          submissionUrl.pathname !== artworkUrl.pathname || submissionUrl.search !== '?file=submission' ||
          JSON.stringify(expected) !== JSON.stringify(received)) {
        throw storageError('PROOF_STORAGE_RECEIPT_INVALID', 'The artwork service could not verify all source files. Your design is preserved; please contact ERS or try again later.');
      }
    }
    return {
      submissionId: String(body.submissionId),
      reference: String(body.reference),
      artworkUrl: artworkUrl.href,
      submissionUrl: body.submissionUrl || '',
      sourceIds: body.sourceIds || [],
      storedAt: new Date().toISOString()
    };
  }

  function create(endpoint) {
    var endpointUrl;
    try {
      endpointUrl = new URL(endpoint);
    } catch (error) {
      throw storageError('PROOF_STORAGE_ENDPOINT_INVALID', 'The secure artwork service is temporarily unavailable. Your work is saved; please try again shortly.');
    }
    if (endpointUrl.protocol !== 'https:' || endpointUrl.pathname !== '/submit') {
      throw storageError('PROOF_STORAGE_ENDPOINT_INVALID', 'The secure artwork service is temporarily unavailable. Your work is saved; please try again shortly.');
    }

    var activeController = null;
    var destroyed = false;

    function diagnostic(error) {
      if (!window.console || !console.error) return;
      console.error('[ERS proof storage]', {
        code: error.code || 'PROOF_STORAGE_UNKNOWN_ERROR',
        details: error.details || null
      });
    }

    function submit(payload) {
      if (destroyed) return Promise.reject(storageError('PROOF_STORAGE_DESTROYED', 'The proof request was interrupted. Please try again.'));
      if (activeController) return Promise.reject(storageError('PROOF_STORAGE_ACTIVE', 'Your proof request is already being submitted.'));
      if (
        !payload || !payload.attemptId || !payload.reference || !payload.email ||
        !payload.metadata || !(payload.artworkFile instanceof File)
      ) {
        return Promise.reject(storageError('PROOF_STORAGE_PAYLOAD_INVALID', 'The proof request is missing required information. Your work is saved; please try again.'));
      }

      var formData = new FormData();
      formData.append('attemptId', payload.attemptId);
      formData.append('reference', payload.reference);
      formData.append('email', payload.email);
      formData.append('metadata', payload.metadata);
      formData.append('artwork', payload.artworkFile, payload.artworkFile.name);
      (payload.sourceFiles || []).forEach(function (source) {
        formData.append('source:' + source.id, source.file, source.file.name);
      });

      var controller = new AbortController();
      activeController = controller;
      var timeout = window.setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT);

      return window.fetch(endpointUrl.href, {
        method: 'POST',
        body: formData,
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        signal: controller.signal
      }).then(function (response) {
        return response.json().catch(function () { return null; }).then(function (body) {
          if (!response.ok || !body || body.ok !== true) {
            throw storageError(
              body && body.code ? body.code : 'PROOF_STORAGE_REJECTED',
              response.status === 409
                ? 'This proof reference no longer matches the current design. Please make one small edit and try again.'
                : 'Your artwork could not be stored securely. Your work is saved; please try again.',
              { status: response.status, responseCode: body && body.code ? body.code : null }
            );
          }
          return verifyReceipt(payload, body, endpointUrl.href);
        });
      }).catch(function (error) {
        var normalized = error;
        if (error && error.name === 'AbortError') {
          normalized = storageError('PROOF_STORAGE_TIMEOUT', 'Artwork storage took too long. Your work is saved; please check your connection and try again.');
        } else if (!error || !error.code) {
          normalized = storageError('PROOF_STORAGE_NETWORK_ERROR', 'Your artwork could not reach the secure storage service. Your work is saved; please check your connection and try again.');
        }
        diagnostic(normalized);
        throw normalized;
      }).finally(function () {
        window.clearTimeout(timeout);
        if (activeController === controller) activeController = null;
      });
    }

    function destroy() {
      destroyed = true;
      if (activeController) activeController.abort();
      activeController = null;
    }

    return { submit: submit, destroy: destroy, endpoint: endpointUrl.href };
  }

  window.ErsProofStorage = {
    create: create, createReference: createReference, metadata: metadata,
    buildProofCanvas: buildProofCanvas, protectContact: protectContact, verifyReceipt: verifyReceipt
  };
}());
